import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSprings, animated } from "@react-spring/web";

const HowIWorkSection = () => {
  const items = [
    {
      title: (
        <>
          1. Первая диагностическая сессия (
          <Typography
            component="span"
            sx={{ color: "#2cc46d", fontWeight: "bold", fontSize: "20px" }}
          >
            бесплатно
          </Typography>
          )
        </>
      ),
      description:
        "Мы выявляем ваши ключевые цели и вызовы, анализируем текущую ситуацию и определяем, какие шаги приведут к успеху.",
    },
    {
      title: "2. Индивидуальная стратегия",
      description: "Мы подбираем подходящий формат работы для вашего бизнеса:",
      list: [
        "Сессия для решения точечных задач.",
        "Стратегические сессии для долгосрочного роста.",
        "Комплексное сопровождение для масштабирования.",
      ],
    },
    {
      title: "3. Прогресс с первых шагов",
      description: "После первой полноценной сессии вы получите:",
      list: [
        "Практические инструменты для устранения узких мест.",
        "Конкретный план действий, который даст ощутимые результаты.",
        "Чёткую уверенность в том, что вы движетесь в правильном направлении.",
      ],
    },
  ];

  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const springs = useSprings(
    items.length,
    items.map((_, index) => ({
      from: { opacity: 0, transform: "translateX(-50px)" },
      to: inView
        ? { opacity: 1, transform: "translateX(0px)" }
        : { opacity: 0, transform: "translateX(-50px)" },
      delay: index * 2000,
      config: { tension: 80, friction: 20 },
    }))
  );

  return (
    <Box
      ref={sectionRef}
      sx={{
        background: "#000",
        color: "#fff",
        py: { xs: 6, md: 8 },
        px: { xs: 7, md: 6 },
        overflowX: "hidden",
        minHeight: { xs: "auto", md: "80vh" },
        display: "flex",
        flexDirection: "column",
        justifyContent: { xs: "flex-start", md: "center" },
        alignItems: { xs: "stretch", md: "center" },
      }}
    >
      <Box sx={{ maxWidth: "900px", mx: "auto", textAlign: "center", mb: 6 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            mb: 3,
            lineHeight: 1.3,
            fontSize: {
              xs: "2.1rem",
              md: "3.3rem",
            },
          }}
        >
          Как я работаю:
        </Typography>
      </Box>
      <Grid
        container
        spacing={4}
        sx={{
          width: "100%",
          maxWidth: "500px",
          justifyContent: { xs: "flex-start", md: "center" },
        }}
      >
        {springs.map((style, index) => (
          <animated.div key={index} style={style}>
            <Grid
              item
              xs={12}
              md={12}
              sx={{
                textAlign: { xs: "left", md: "center" },
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
                {items[index].title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {items[index].description}
              </Typography>
              {items[index].list && (
                <List
                  sx={{
                    display: { xs: "block", md: "inline-block" },
                    textAlign: { xs: "left", md: "center" },
                    mb: { xs: 0, md: 4 },
                    padding: 0,
                  }}
                >
                  {items[index].list.map((listItem, subIndex) => (
                    <ListItem
                      key={subIndex}
                      sx={{
                        pl: 0,
                        justifyContent: { xs: "flex-start", md: "center" },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: "30px",
                          color: "#2cc46d",
                        }}
                      >
                        <CheckCircleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={listItem}
                        primaryTypographyProps={{
                          sx: { lineHeight: "1.5", fontSize: "1rem" },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </animated.div>
        ))}
      </Grid>
    </Box>
  );
};

export default HowIWorkSection;
